<template>
  <div class="categories-configuration">
    <div class="no-content" v-if="categories.length === 0">
      <span>{{ labels.notFound }}</span>
    </div>
    <div
      v-for="(category, cIdx) in categories"
      :key="`category_${cIdx}`"
      class="category-row"
    >
      <div :class="[showBorder(cIdx)]">
        <b-card
          :header-class="{ 'category-card-header': editable }"
          class="draggable-category"
          :draggable="editable"
          @dragstart="indexPick = cIdx"
          @dragenter="indexDrop = cIdx"
          @dragover="indexOver = cIdx"
          @dragleave="indexOver = undefined"
          @drop="indexOver = undefined"
          @dragend="moveCategory(indexPick, indexDrop)"
        >
          <header slot="header">
            <b-row>
              <b-col md="12">
                <div class="float-left category-header">
                  <i
                    class="material-icons mr-2 drag-indicator clickable"
                    v-if="editable"
                  >
                    drag_indicator
                  </i>
                  <span>
                    {{ cIdx + 1 }}&deg; {{ labels.category }}:
                    <span
                      v-for="(language, idx) in availableLanguages"
                      :key="`label_${cIdx}_${idx}`"
                    >
                      <span v-if="isLanguageSelected(idx)">
                        {{ category.label[language] }}
                      </span>
                    </span>
                  </span>
                </div>
                <i
                  v-if="editable"
                  :title="labels.delete"
                  @click="$emit('removeCategory', cIdx)"
                  class="material-icons clickable float-right"
                >
                  delete_forever
                </i>
              </b-col>
            </b-row>
          </header>
          <b-row>
            <b-col class="pb-3" cols="12" xl="6">
              <div
                class="pb-1"
                v-for="(language, index) in availableLanguages"
                :key="`category_label_${cIdx}_${index}`"
              >
                <b-form-group
                  v-if="isLanguageSelected(index)"
                  :description="editable ? labels.name.description : ''"
                >
                  <b-form-input
                    v-if="editable"
                    class="name-form-group custom-survey-form-input"
                    :placeholder="labels.name.placeholder"
                    :value="category.label[language]"
                    @input="
                      changeLanguageListProperty(
                        $event,
                        'categories',
                        cIdx,
                        'label',
                        language
                      )
                    "
                  />
                  <p v-else class="readonly-input">
                    {{ category.label[language] }}
                  </p>
                  <label class="custom-input-label">
                    <img
                      :src="
                        getLanguageIcon(availableLanguages[selectedLanguage])
                      "
                      class="language-icon"
                    />
                    {{ labels.name.title }}
                  </label>
                </b-form-group>
              </div>
              <fieldset
                v-if="editable"
                class="form-group-custom mt-2"
                :aria-describedby="labels.image.description"
              >
                <legend tabindex="-1" class="col-form-label pt-0">
                  {{ labels.image.title }}
                </legend>
                <div tabindex="-1" role="group">
                  <treeselect
                    ref="treeselect"
                    name="image-category-select"
                    :placeholder="labels.image.select"
                    clearValueText=""
                    :value="category.image"
                    :options="images.map(i => ({ id: i, label: i }))"
                    :multiple="multiple"
                    :clearable="clearable"
                    :searchable="searchable"
                    :open-on-click="openOnClick"
                    :open-on-focus="openOnFocus"
                    :close-on-select="closeOnSelect"
                    :always-open="alwaysOpen"
                    @input="
                      changeListProperty($event, 'categories', cIdx, 'image')
                    "
                  >
                    <label
                      slot="option-label"
                      slot-scope="{
                        node,
                        shouldShowCount,
                        count,
                        labelClassName,
                        countClassName,
                      }"
                      :class="labelClassName"
                    >
                      <img
                        class="category-image-icon mr-2"
                        :src="getImageUrl(node.id)"
                      />
                      <span>
                        {{ labels.image.label }}
                        {{ images.indexOf(node.id) + 1 }}
                      </span>
                      <span v-if="shouldShowCount" :class="countClassName">
                        ({{ count }})
                      </span>
                    </label>
                    <div slot="value-label" slot-scope="{ node }">
                      <img
                        class="category-image-icon mr-2"
                        :src="getImageUrl(node.id)"
                      />
                      <span class="category-image-label">
                        {{ labels.image.label }}
                        {{ images.indexOf(node.id) + 1 }}
                      </span>
                    </div>
                  </treeselect>
                  <small tabindex="-1" class="form-text text-muted">
                    {{ labels.image.description }}
                  </small>
                </div>
              </fieldset>
              <b-form-group v-else>
                <b-form-input
                  v-if="!category.image"
                  class="readonly-input"
                  :value="labels.image.empty"
                  readonly
                />
                <v-select
                  v-else
                  class="readonly-input"
                  :value="category.image"
                  disabled
                >
                  <template slot="selected-option" slot-scope="option">
                    <div class="tag-wrap">
                      <img class="tag-image" :src="getImageUrl(option.label)" />
                      <span class="tag-label">
                        {{ labels.image.label }}
                        {{ images.indexOf(option.label) + 1 }}
                      </span>
                    </div>
                  </template>
                </v-select>
                <label class="custom-input-label">
                  {{ labels.image.title }}
                </label>
              </b-form-group>
            </b-col>
            <b-col cols="12" xl="6">
              <div
                v-for="(language, index) in availableLanguages"
                :key="`category_${cIdx}_${index}`"
              >
                <b-form-group
                  v-if="isLanguageSelected(index)"
                  :description="editable ? labels.description.description : ''"
                >
                  <b-form-textarea
                    v-if="editable"
                    class="custom-survey-form-input survey-description"
                    :disabled="!editable"
                    :placeholder="
                      editable ? labels.description.placeholder : ''
                    "
                    rows="5"
                    :value="category.description[language]"
                    @input="
                      changeLanguageListProperty(
                        $event,
                        'categories',
                        cIdx,
                        'description',
                        language
                      )
                    "
                  />
                  <p v-else class="readonly-input">
                    {{
                      category.description[language]
                        ? category.description[language]
                        : labels.description.placeholderEmpty
                    }}
                  </p>
                  <label class="custom-input-label">
                    <img
                      :src="
                        getLanguageIcon(availableLanguages[selectedLanguage])
                      "
                      class="language-icon"
                    />
                    {{ labels.description.title }}
                  </label>
                </b-form-group>
              </div>
            </b-col>
          </b-row>
          <b-row v-if="questions.length > 0 && category.questions.length > 0">
            <b-col cols="12" xl="12">
              <hr />
              <div class="mb-2">
                <small>{{ labels.questions }}:</small>
              </div>
              <div
                class="mt-2"
                v-for="(question, index) in category.questions"
                :key="question.question_id"
              >
                {{ index + 1 }}.
                {{
                  findQuestion(question.question_id).text[
                    availableLanguages[selectedLanguage]
                  ]
                }}
                <i
                  v-if="editable"
                  :title="labels.delete"
                  @click="
                    $emit(
                      'removeQuestionFromCategory',
                      category.category_id,
                      question.question_id
                    )
                  "
                  class="material-icons clickable float-right"
                >
                  delete_forever
                </i>
              </div>
            </b-col>
          </b-row>
        </b-card>
      </div>
    </div>
    <div v-if="editable">
      <b-button
        variant="link"
        class="float-left text-secondary mt-4"
        @click="$emit('addCategory')"
      >
        <i class="material-icons">add</i>
        {{ labels.add }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

const props = {
  categories: {
    type: Array,
  },
  questions: {
    type: Array,
  },
  availableLanguages: {
    type: Array,
  },
  selectedLanguage: {
    type: Number,
  },
  editable: {
    type: Boolean,
    default: true,
  },
}

export default {
  props,
  components: {
    Treeselect,
    vSelect: () => import('vue-select'),
  },
  data() {
    return {
      indexPick: undefined,
      indexDrop: undefined,
      indexOver: undefined,
      multiple: false,
      clearable: true,
      searchable: false,
      openOnClick: true,
      openOnFocus: true,
      closeOnSelect: true,
      alwaysOpen: false,
      images: [
        'pragmatism',
        'selfDiscipline',
        'consciousnessCosts',
        'organizationalGrowth',
        'financialStability',
        'safetyWork',
        'employeeHealth',
        'friendship',
        'openCommunication',
        'effectiveCommunication',
        'loyalty',
        'recognition',
        'respect',
        'customerSatisfaction',
        'ambition',
        'selfConfidence',
        'competence',
        'professionalGrowth',
        'excellence',
        'experience',
        'focusGoals',
        'focusProcess',
        'focusResults',
        'brandImage',
        'organization',
        'planning',
        'productivity',
        'professionalism',
        'competitiveness',
        'proud',
        'adaptability',
        'learningFromMistakes',
        'continuousLearning',
        'acceptRisks',
        'autonomy',
        'empowerment',
        'entrepreneurship',
        'balanceHomeWork',
        'flexibility',
        'initiative',
        'innovation',
        'continuousImprovement',
        'perseverance',
        'protagonism',
        'resilience',
        'responsibility',
        'teamWork',
        'curiosity',
        'socialResponsability',
        'valuingDiversity',
        'influence',
        'positiveAttitude',
        'coherence',
        'commitment',
        'confidence',
        'engagement',
        'generosity',
        'honesty',
        'meritocracy',
        'passion',
        'transparency',
        'sharedValues',
        'welfare',
        'coachingMentoring',
        'collaborationCustomer',
        'collaborationAreas',
        'developingPeople',
        'empathy',
        'listeningOther',
        'makeDifferenceOtros',
        'intuition',
        'partnership',
        'volunteerWork',
        'leadershipDevelopment',
        'leadership',
        'deongoTermFocus',
        'humility',
        'holisticThinking',
        'sustainability',
        'agility',
        'alertness',
        'fun',
        'attentiontoDetail',
        'belonging',
        'frugality',
        'guidance',
        'purpose',
        'tradition',
        'knowledge',
        'celebrate',
        'problemSolvers',
        'simplicity',
      ],
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
    }),
    labels() {
      return this.resources.component.customSurveys.create.categories
    },
  },
  methods: {
    findQuestion(question_id) {
      return this.questions.find(q => q.question_id === question_id)
    },
    getImageUrl(image) {
      return `https://static.teamculture.com.br/images/culture/${image}.svg`
    },
    isLanguageSelected(language) {
      return this.selectedLanguage === language
    },
    getLanguageIcon(language) {
      return `/icons/languages/${language}.svg`
    },
    changeLanguageListProperty(value, collection, index, property, language) {
      this.$emit('changeLanguageListProperty', {
        value,
        collection,
        index,
        property,
        language,
      })
    },
    changeListProperty(value, collection, index, property) {
      this.$emit('changeListProperty', {
        value,
        collection,
        index,
        property,
      })
    },
    showBorder(i) {
      if (this.indexOver !== i || i === this.indexPick) return ''
      return i < this.indexPick ? 'show-top-border' : 'show-bot-border'
    },
    moveCategory(from, to) {
      if (to < 0 || to >= this.categories.length) return
      this.$emit('moveCategory', { from, to })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

[draggable] {
  -moz-user-select: none;
  -khtml-user-select: none;
  -webkit-user-select: none;
  user-select: none;
  -khtml-user-drag: element;
  -webkit-user-drag: element;
}

.categories-configuration {
  background-color: map-get($theme-colors, 'white02');

  .no-content {
    text-align: center;
    padding-top: 50px;
    padding-bottom: 40px;
  }

  .show-top-border {
    border-top: 1px solid map-get($theme-colors, 'primary');
  }

  .show-bot-border {
    border-bottom: 1px solid map-get($theme-colors, 'primary');
  }

  .draggable-category {
    border: 0 !important;

    .form-group-custom {
      .col-form-label {
        color: map-get($theme-colors, 'primary');
        padding-bottom: 0px;
        font-size: 12px;
        margin-left: 20px;
      }

      .form-text {
        margin-left: 15px;
      }
    }

    .category-header {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .material-icons {
      line-height: 20px;
      font-size: 20px;
      padding: 3px 0px;
    }

    .card-header {
      font-size: 14px;
      font-weight: bold;
      background-color: map-get($theme-colors, 'white02');

      &.category-card-header {
        &:hover {
          cursor: pointer;
          background-color: map-get($theme-colors, 'light') !important;

          .material-icons {
            opacity: 1;
          }
        }
      }
    }

    .card-body {
      border: 1px solid var(--gray07) !important;

      .language-icon {
        width: 15px;
        height: 15px;
      }

      .category-image-icon {
        margin-top: 10px;
        width: 26px;
        height: 26px;
        border-radius: 50px;
      }

      .category-image-label {
        padding-top: 10px;
        vertical-align: middle;
      }

      .survey-description {
        height: auto !important;
      }

      .readonly-input {
        height: auto !important;
        background-color: map-get($theme-colors, 'white') !important;
        border-color: map-get($theme-colors, 'white') !important;
        color: #6c757d !important;
      }

      p.readonly-input {
        margin-top: 10px;
        margin-left: 20px;
        margin-right: 10px;
        font-size: 1rem;
      }
    }
  }
}

/deep/ .vue-treeselect {
  .vue-treeselect__menu {
    color: map-get($theme-colors, 'primary') !important;
    border-radius: 10px !important;
    overflow-x: auto;
  }

  .vue-treeselect__checkbox--checked {
    border-color: map-get($theme-colors, 'primary') !important;
    background: map-get($theme-colors, 'primary') !important;
  }

  .vue-treeselect__control {
    border-radius: 10px !important;
    height: 48px;
  }

  .vue-treeselect__input {
    background: map-get($theme-colors, 'white') !important;
    font-size: 14pt;
  }

  .vue-treeselect__placeholder {
    margin: 7px;
    color: map-get($theme-colors, 'gray03') !important;
    font-size: 10pt;
  }

  .vue-treeselect__multi-value-item {
    border-radius: 10px !important;
    background-color: map-get($theme-colors, 'gray08');
    color: map-get($theme-colors, 'primary') !important;

    .vue-treeselect__value-remove {
      color: map-get($theme-colors, 'primary') !important;
    }

    .vue-treeselect__multi-value-label {
      white-space: normal;
    }
  }

  .vue-treeselect__label-container {
    &:hover {
      .vue-treeselect__checkbox--unchecked {
        border-color: map-get($theme-colors, 'primary');
      }
    }

    .vue-treeselect__label {
      overflow: inherit;
    }
  }

  .vue-treeselect__option--selected {
    background-color: map-get($theme-colors, 'gray08');
  }
}
</style>
